// بطاقة «موظف الشهر» على شاشة الدخول.
//
// تُقرأ من مسار عام بلا جلسة، ولا يصلها إلا ما أقرّه المالك للعرض: الاسم والشهر والرسالة
// والصورة. لا بريد ولا هاتف ولا رقم مالي — والخادم لا يرسلها أصلًا، فليست بياناتٍ تُخفى هنا.
//
// وفشل النداء لا يُعرض للزائر ولا يمنع الدخول: البطاقة تختفي بهدوء وتبقى شاشة الدخول كما هي.
import { useEffect, useState } from 'react';

const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                   'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

/** «2026-09-01» ← «سبتمبر 2026». */
export function monthLabel(iso: string): string {
  const [y, m] = iso.split('-');
  const i = Number(m) - 1;
  return i >= 0 && i < 12 ? `${MONTHS_AR[i]} ${y}` : iso;
}

interface Public { month: string; employee: { name: string; message: string | null; hasPhoto: boolean; totalOps: number | null } | null }

/** البادئة نفسها التي يستعملها عميل الـAPI: نفس الأصل على الويب، ومطلق داخل التطبيق. */
const BASE = (import.meta.env?.VITE_API_URL as string | undefined) ?? '/api';

export function EmployeeOfMonthCard() {
  const [data, setData] = useState<Public | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`${BASE}/public/employee-of-month`);
        if (!res.ok) throw new Error('unavailable');
        // نفس أسلوب عميل الـAPI: نصّ ثم تحليل — فلا اعتماد على res.json()
        const j = JSON.parse(await res.text()) as Public;
        if (alive) setData(j);
      } catch {
        if (alive) setFailed(true);           // لا رسالة تقنية للزائر
      }
    })();
    return () => { alive = false; };
  }, []);

  if (failed) return null;

  return (
    <section className="w-full max-w-md text-center" aria-label="موظف الشهر">
      {/* العنوان وحده تغيّر إلى «Top Achiever» بقرار المالك — ولا شيء غيره: نفس التصميم
          والمقاس واللون والخطّ، ونفس الموظف والشهر والبيانات ومسارها. */}
      <p className="text-base font-semibold tracking-wide text-white/90 sm:text-lg">
        <span aria-hidden="true">★ </span>Top Achiever
      </p>
      {data && <p className="mt-1 text-sm text-white/75">{monthLabel(data.month)}</p>}

      {!data ? (
        <div className="mt-6 h-44" aria-hidden="true" />
      ) : data.employee === null ? (
        <div className="mt-6 rounded-2xl border border-white/20 bg-white/10 px-6 py-10 backdrop-blur">
          {/* الفائز يُحسب من عمليات الشهر المنقضي، فشهر بلا عمليات لا فائز له — ويُقال صراحةً باسم شهره */}
          <p className="text-base text-white/85">لا توجد عمليات مسجَّلة في {monthLabel(data.month)}</p>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-white/20 bg-white/10 px-6 py-9 backdrop-blur sm:px-8 sm:py-10">
          {data.employee.hasPhoto ? (
            <img
              src={`${BASE}/public/employee-of-month/photo`}
              alt={data.employee.name}
              className="mx-auto h-40 w-40 rounded-full border-4 border-white/70 object-cover shadow-xl sm:h-48 sm:w-48"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="mx-auto flex h-40 w-40 items-center justify-center rounded-full border-4 border-white/70 bg-white/20 text-5xl font-bold text-white sm:h-48 sm:w-48">
              {data.employee.name.trim().charAt(0)}
            </div>
          )}
          <p className="mt-5 text-2xl font-bold text-white sm:text-3xl">{data.employee.name}</p>
          {data.employee.message && (
            <p className="mt-3 text-base leading-loose text-white/90 sm:text-lg">«{data.employee.message}»</p>
          )}
        </div>
      )}
    </section>
  );
}
